import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import {
  fetchNewsByCategory,
  fetchCategoryHighlights,
  fetchCategories,
} from "../../api/newsApi";
import { shareContent } from "../../utils/share";
import "./CategoryPage.css";

const PAGE_SIZE = 12;

function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [newsList, setNewsList] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [trending, setTrending] = useState([]);
  const [tags, setTags] = useState([]);
  const [highlightsLoading, setHighlightsLoading] = useState(true);

  const [shareMessage, setShareMessage] = useState(null);

  const [categoryName, setCategoryName] = useState(() =>
    slug.replace(/-/g, " "),
  );

  useEffect(() => {
    let isMounted = true;
    setCategoryName(slug.replace(/-/g, " "));

    fetchCategories()
      .then((cats) => {
        if (!isMounted) return;
        const match = cats.find((c) => c.slug === slug);
        if (match) setCategoryName(match.name);
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [slug]);

  const loadCategoryNews = useCallback(
    async (pageToLoad, replace) => {
      replace ? setLoading(true) : setLoadingMore(true);
      setError(null);
      try {
        const data = await fetchNewsByCategory(slug, {
          page: pageToLoad,
          limit: PAGE_SIZE,
        });
        setTotalPages(data.totalPages || 1);
        setNewsList((prev) =>
          replace ? data.items : [...prev, ...data.items],
        );
      } catch (err) {
        setError("We couldn't load this category right now.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [slug],
  );

  const loadHighlights = useCallback(async () => {
    setHighlightsLoading(true);
    try {
      const data = await fetchCategoryHighlights(slug);
      setTrending(data.trending);
      setTags(data.tags);
    } catch (err) {
      setTrending([]);
      setTags([]);
    } finally {
      setHighlightsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    setPage(1);
    loadCategoryNews(1, true);
    loadHighlights();
  }, [slug, loadCategoryNews, loadHighlights]);

  function handleLoadMore() {
    const nextPage = page + 1;
    setPage(nextPage);
    loadCategoryNews(nextPage, false);
  }

  function flashShareMessage(result) {
    if (result.shared) return;
    setShareMessage(
      result.copied ? "Link copied to clipboard" : "Couldn't share this link",
    );
    setTimeout(() => setShareMessage(null), 2500);
  }

  async function handleShareCategory() {
    const result = await shareContent({
      title: `${categoryName} - Today Talks`,
      text: `Latest ${categoryName} news on Today Talks`,
      url: window.location.href,
    });
    flashShareMessage(result);
  }

  async function handleShareArticle(news) {
    const url = `${window.location.origin}/news/${news.categorySlug}/${news.id}`;
    const result = await shareContent({
      title: news.title,
      text: news.description,
      url,
      imageUrl: news.image,
    });
    flashShareMessage(result);
  }

  return (
    <main className="tt-catpage-wrap">
      <div className="tt-catpage-heading">
        <span className="tt-catpage-title-rule" aria-hidden="true" />
        <h1 className="tt-catpage-title">{categoryName}</h1>
        <span className="tt-catpage-title-rule" aria-hidden="true" />
      </div>

      {loading && <Loader label="Loading category..." />}
      {!loading && error && (
        <ErrorMessage
          message={error}
          onRetry={() => loadCategoryNews(1, true)}
        />
      )}

      {!loading && !error && (
        <div className="tt-catpage-layout">
          {/* ---- Left: Trending ---- */}
          <aside className="tt-catpage-trending">
            <h2 className="tt-catpage-trending-title">Trending News</h2>
            {highlightsLoading ? (
              <Loader label="Loading trending..." />
            ) : trending.length > 0 ? (
              <ol className="tt-catpage-trending-list">
                {trending.map((item, index) => (
                  <li
                    key={`${item.categorySlug}-${item.id}`}
                    className="tt-catpage-trending-item"
                  >
                    <span className="tt-catpage-trending-num">{index + 1}</span>
                    <Link
                      to={`/news/${item.categorySlug}/${item.id}`}
                      className="tt-catpage-trending-link"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="tt-catpage-trending-empty">
                No trending stories in this category right now.
              </p>
            )}
          </aside>

          {/* ---- Center: Latest news list ---- */}
          <section className="tt-catpage-list-section">
            <div className="tt-catpage-list-header">
              <h2 className="tt-catpage-list-title">Latest News</h2>
              <button
                type="button"
                className="tt-catpage-share-btn"
                onClick={handleShareCategory}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="15"
                  height="15"
                  aria-hidden="true"
                >
                  <circle
                    cx="18"
                    cy="5"
                    r="2.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <circle
                    cx="6"
                    cy="12"
                    r="2.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <circle
                    cx="18"
                    cy="19"
                    r="2.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <line
                    x1="8.2"
                    y1="10.8"
                    x2="15.8"
                    y2="6.2"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <line
                    x1="8.2"
                    y1="13.2"
                    x2="15.8"
                    y2="17.8"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                </svg>
                Share
              </button>
            </div>

            {shareMessage && (
              <p className="tt-catpage-share-msg" role="status">
                {shareMessage}
              </p>
            )}

            {newsList.length > 0 ? (
              <ul className="tt-catpage-list">
                {newsList.map((news) => {
                  const detailsPath = `/news/${news.categorySlug}/${news.id}`;
                  return (
                    <li
                      key={`${news.categorySlug}-${news.id}`}
                      className="tt-catpage-list-item"
                    >
                      <Link to={detailsPath} className="tt-catpage-list-media">
                        {news.image ? (
                          <img
                            src={news.image}
                            alt={news.title}
                            loading="lazy"
                          />
                        ) : (
                          <div
                            className="tt-catpage-list-media-fallback"
                            aria-hidden="true"
                          />
                        )}
                      </Link>

                      <div className="tt-catpage-list-body">
                        <Link
                          to={detailsPath}
                          className="tt-catpage-list-item-title-link"
                        >
                          <h3 className="tt-catpage-list-item-title">
                            {news.title}
                          </h3>
                        </Link>
                        {news.timeAgo && (
                          <p className="tt-catpage-list-meta">{news.timeAgo}</p>
                        )}
                        {news.description && (
                          <p className="tt-catpage-list-desc">
                            {news.description}
                          </p>
                        )}

                        <button
                          type="button"
                          className="tt-catpage-list-share-btn"
                          onClick={() => handleShareArticle(news)}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            width="13"
                            height="13"
                            aria-hidden="true"
                          >
                            <circle
                              cx="18"
                              cy="5"
                              r="2.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                            />
                            <circle
                              cx="6"
                              cy="12"
                              r="2.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                            />
                            <circle
                              cx="18"
                              cy="19"
                              r="2.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                            />
                            <line
                              x1="8.2"
                              y1="10.8"
                              x2="15.8"
                              y2="6.2"
                              stroke="currentColor"
                              strokeWidth="1.8"
                            />
                            <line
                              x1="8.2"
                              y1="13.2"
                              x2="15.8"
                              y2="17.8"
                              stroke="currentColor"
                              strokeWidth="1.8"
                            />
                          </svg>
                          Share
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="tt-catpage-list-empty">
                No latest news available in this category right now.
              </p>
            )}

            {page < totalPages && (
              <div className="tt-catpage-loadmore">
                {loadingMore ? (
                  <Loader label="Loading more..." />
                ) : (
                  <button
                    type="button"
                    className="tt-catpage-loadmore-btn"
                    onClick={handleLoadMore}
                  >
                    Load more
                  </button>
                )}
              </div>
            )}
          </section>

          {/* ---- Right: Quick Links (tags) ---- */}
          <aside className="tt-catpage-quicklinks">
            <h2 className="tt-catpage-quicklinks-title">
              Quick Links <span aria-hidden="true">&rsaquo;</span>
            </h2>
            {highlightsLoading ? (
              <Loader label="Loading quick links..." />
            ) : tags.length > 0 ? (
              <div className="tt-catpage-quicklinks-pills">
                {tags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className="tt-catpage-quicklink-pill"
                    onClick={() =>
                      navigate(`/search?q=${encodeURIComponent(tag)}`)
                    }
                  >
                    {tag}
                  </button>
                ))}
              </div>
            ) : (
              <p className="tt-catpage-quicklinks-empty">
                No quick links available right now.
              </p>
            )}
          </aside>
        </div>
      )}
    </main>
  );
}

export default CategoryPage;
